import {
  ChangeDetectionStrategy,
  Component, computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {firstValueFrom, Observable, tap} from 'rxjs';
import {ValuationTabComponent} from '../valuation-tab.component';
import {TaxBillResponse} from '../../../models/valuation.models';
import {FileDropZoneComponent} from '../../../shared/components/file-drop-zone/file-drop-zone.component';
import {TaxRate} from '../../../models/tax_rate.models';

@Component({
  selector: 'app-tax-bill-tab',
  templateUrl: './tax-bill-tab.component.html',
  styleUrl: './tax-bill-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [FileDropZoneComponent]
})
export class TaxBillTabComponent extends ValuationTabComponent<TaxBillResponse> {
  protected readonly tabTitle = 'Tax Bill';
  protected readonly imageUrl = signal<string | null>(null);
  protected readonly taxRate = signal<TaxRate>(new TaxRate(null));
  protected readonly uploadAccept = computed(() => {
    return (this.data()?.upload_formats ?? []).map((f) => `.${f}`)
  })

  private readonly dropZone = viewChild<FileDropZoneComponent>('dropZone');

  constructor() {
    super();

    inject(DestroyRef).onDestroy(() => {
      const url = this.imageUrl();
      if (url) URL.revokeObjectURL(url);
    });

    effect(() => {
      const current = this.data()?.tax_rate ?? -1;
      this.valid.set(current >= 0 && current <= 50);
    });

    effect(() => {
      const current = this.data();
      this.editing.set(typeof current?.tax_rate !== 'number')
      this.updateTaxRate(current?.tax_rate)
      // untracked: loadImage reads/writes the imageUrl signal internally --
      // without this, that write would register imageUrl as a dependency of
      // this same effect (since the read happens synchronously inside the
      // effect's call stack), and every future imageUrl.set() would
      // re-trigger this effect -> infinite re-fetch loop.
      untracked(() => void this.loadImage(current));
    });
  }

  protected override loadData(): Observable<TaxBillResponse> {
    console.debug('[TaxBillTab] loading status', {sessionId: this.sessionId()});
    return this.valuationService.getTaxBill(this.sessionId());
  }

  protected updateTaxRate(raw: string | number | null | undefined): void {
    this.taxRate.set(new TaxRate(raw))
  }

  protected openFallbackUrl(): void {
    const url = this.data()?.fallback_url;
    if (url) window.open(url, '_blank', 'noopener');
  }

  protected async viewBill(): Promise<void> {
    if (!this.data()?.has_source) return;

    const sessionId = this.sessionId();
    console.info('[TaxBillTab] fetching source document', {sessionId});
    const blob = await this.load(this.valuationService.getTaxBillSource(sessionId))

    if (blob) {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  }

  protected async onFileSelected(file: File | null): Promise<void> {
    if (file) await this.upload(file);
  }

  protected async upload(file: File): Promise<void> {
    const sessionId = this.sessionId();

    const formData = new FormData();
    formData.append('file', file);
    await this.load(
      this.valuationService.uploadTaxBill(sessionId, formData).pipe(
        tap(response => {
          console.info('[TaxBillTab] upload succeeded', {
            sessionId,
            source: response.source,
            taxRate: response.tax_rate
          });
          this.setData(response);
        })
      ),
      {error: 'Failed to upload tax bill.'},
    );
    this.dropZone()!.reset();
  }

  protected async save(): Promise<void> {
    const rate = this.taxRate()
    if (!rate.valid) return;
    const sessionId = this.sessionId();
    const response = await this.load(
      this.valuationService.saveTaxBill(sessionId, rate.value),
      {error: 'Failed to save tax rate.'},
    );
    if (response) {
      this.setData(response);
    }
  }

  protected cancel(): void {
    this.updateTaxRate(this.data()?.tax_rate)
    this.editing.set(false)
  }

  protected async refresh(): Promise<void> {
    const sessionId = this.sessionId();
    console.info('[TaxBillTab] refreshing (bypassing cache)', {sessionId});
    const response = await this.load(
      this.valuationService.refreshTaxBill(sessionId),
      {error: 'Failed to refresh tax bill.'},
    );
    if (response) {
      console.info('[TaxBillTab] refresh succeeded', {sessionId, source: response.source});
      this.setData(response);
    }
  }

  private async loadImage(current: TaxBillResponse | null): Promise<void> {
    const previous = this.imageUrl();
    if (previous) {
      URL.revokeObjectURL(previous);
      this.imageUrl.set(null);
    }
    if (!current?.source) return;
    const sessionId = this.sessionId();
    console.debug('[TaxBillTab] fetching snapshot image', {sessionId});
    const start = performance.now();
    try {
      const blob = await firstValueFrom(this.valuationService.getTaxBillImage(sessionId));
      console.debug('[TaxBillTab] snapshot image fetched', {
        sessionId,
        elapsedMs: Math.round(performance.now() - start),
        bytes: blob.size,
      });
      this.imageUrl.set(URL.createObjectURL(blob));
    } catch (e) {
      console.error('[TaxBillTab] failed to fetch snapshot image', {sessionId, error: e});
      this.imageUrl.set(null);
    }
  }
}
